import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, ArrowRight, FileText, Archive } from 'lucide-react';
import Navigation from '../../components/Navigation';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const StartTrading = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const features = t('startTrading.features', { returnObjects: true });

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAFA' }}>
      <Navigation />

      {/* Hero Section */}
      <section className="relative text-white py-12 overflow-hidden" style={{ background: 'linear-gradient(135deg, #456A9E 0%, #4A6AA0 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 animate-pulse" style={{ background: 'linear-gradient(135deg, #162440, #456A9E, #4A6AA0)' }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {t('startTrading.heroTitle')}
            </h1>
            <p className="text-xl md:text-2xl mb-8" style={{ color: '#D6E0EE' }}>
              {t('startTrading.heroSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {user ? (
                <>
                  <Link
                    to="/oglasna-tabla-board"
                    className="px-8 py-4 rounded-lg font-semibold transition-colors inline-flex items-center justify-center gap-2"
                    style={{ backgroundColor: '#A8922A', color: '#ffffff' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#7A6A1E'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#A8922A'}
                  >
                    {t('startTrading.viewBoard')}
                    <ArrowRight size={20} />
                  </Link>
                  <Link
                    to="/objavi-ponudu-form"
                    className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors inline-flex items-center justify-center"
                  >
                    {t('startTrading.postWish')}
                  </Link>
                </>
              ) : null}
            </div>
            {user && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-4">
                <Link
                  to="/my-negotiations-moja-pregovaranja"
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/30 px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center justify-center gap-2"
                >
                  <FileText size={18} />
                  {t('startTrading.myNegotiations')}
                </Link>
                <Link
                  to="/my-offers-archive"
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/30 px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center justify-center gap-2"
                >
                  <Archive size={18} />
                  {t('startTrading.offersArchive')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pt-10 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-gray-600 mb-8">{t('startTrading.desc1')}</p>
              <p className="text-lg text-gray-600 mb-8">{t('startTrading.desc2')}</p>
              <p className="text-lg text-gray-600 mb-8">{t('startTrading.desc3')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Array.isArray(features) && features?.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="flex-shrink-0" size={24} style={{ color: '#A8922A' }} />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Featured info card - old gold background */}
            <div className="rounded-2xl p-8 text-white" style={{ background: 'linear-gradient(135deg, #A8922A 0%, #CFB53B 100%)' }}>
              <h3 className="text-3xl font-bold mb-4">{t('startTrading.startToday')}</h3>
              <p className="mb-6" style={{ color: 'rgba(255,255,255,0.9)' }}>{t('startTrading.startTodayDesc')}</p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                    <CheckCircle size={24} />
                  </div>
                  <div>
                    <div className="text-sm" style={{ color: 'rgba(255,255,255,0.85)' }}>{t('startTrading.noHiddenCosts')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                    <Shield size={24} />
                  </div>
                  <div>
                    <div className="text-sm" style={{ color: 'rgba(255,255,255,0.85)' }}>{t('startTrading.protectedTransactions')}</div>
                  </div>
                </div>
              </div>
              {user ? (
                <Link
                  to="/oglasna-tabla-board"
                  className="block w-full bg-white px-6 py-4 rounded-lg font-semibold hover:bg-yellow-50 transition-colors text-center"
                  style={{ color: '#A8922A' }}
                >
                  {t('startTrading.viewMarket')}
                </Link>
              ) : (
                <a
                  href="https://valuebook.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-white px-6 py-4 rounded-lg font-semibold hover:bg-yellow-50 transition-colors text-center"
                  style={{ color: '#23395B' }}
                >
                  {t('startTrading.registerOnApp')}
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 text-white" style={{ background: 'linear-gradient(135deg, #A8922A 0%, #CFB53B 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">{t('startTrading.ctaTitle')}</h2>
          <p className="text-xl mb-8" style={{ color: 'rgba(255,255,255,0.9)' }}>{t('startTrading.ctaSubtitle')}</p>
          <Link
            to={user ? '/objavi-ponudu-form' : '/register'}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold transition-colors"
            style={{ backgroundColor: '#23395B', color: '#ffffff' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#162440'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#23395B'}
          >
            {user ? t('startTrading.ctaBtn') : t('startTrading.ctaBtnGuest')}
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-white py-8" style={{ backgroundColor: '#23395B' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p style={{ color: '#94a3b8' }}>{t('startTrading.footerCopyright')}</p>
        </div>
      </footer>
    </div>
  );
};

export default StartTrading;